import {
  BiSheetUploadView,
  BiUploadFailure,
  BiUploadStatus,
  BiUploadView,
} from '@euler/model/bi/upload';

/** What the file upload's card shows as a whole. */
export type BiUploadPhase =
  /** The file is being read. */
  | { kind: 'reading' }
  /** The file could not be imported at all. */
  | { kind: 'failed'; failure: BiUploadFailure | null }
  /** Nobody chose its sheets in time, or the offer was answered. */
  | { kind: 'expired' }
  /** The same file was imported before: use it, or import it again. */
  | { kind: 'duplicate' }
  /** Which sheets to import. */
  | { kind: 'sheets' }
  /** One section per selected sheet. */
  | { kind: 'sections' }
  | { kind: 'deleted' };

/**
 * The card's phase. A duplicate is offered first while the file waits for
 * a sheet choice; **Import as new** (`importAsNew`) goes on to the sheets.
 */
export function biUploadPhase(
  view: BiUploadView,
  importAsNew: boolean,
): BiUploadPhase {
  switch (view.status) {
    case BiUploadStatus.Uploaded:
      return { kind: 'reading' };
    case BiUploadStatus.NeedsSheetSelection:
      return view.duplicates.length && !importAsNew
        ? { kind: 'duplicate' }
        : { kind: 'sheets' };
    case BiUploadStatus.Failed:
      return { kind: 'failed', failure: view.failure };
    case BiUploadStatus.Expired:
      return { kind: 'expired' };
    case BiUploadStatus.Deleted:
      return { kind: 'deleted' };
    default:
      return { kind: 'sections' };
  }
}

/** What one selected sheet's section shows. */
export type BiSheetPhase =
  | { kind: 'working'; label: string }
  | { kind: 'confirm' }
  | { kind: 'proposals'; draftIds: string[] }
  | { kind: 'failed'; message: string }
  | { kind: 'deleted' };

const kFailures: Record<BiUploadFailure, string> = {
  [BiUploadFailure.File]: '这个文件无法读取，请检查后重新上传。',
  [BiUploadFailure.Interpret]: '没能读懂这个工作表的列，请检查表头后重新上传。',
  [BiUploadFailure.Materialize]: '数据没有保存成功，请稍后重新上传。',
  [BiUploadFailure.Propose]:
    '数据已经保存，但没能挑出推荐图表。可以直接提问来分析它。',
};

/**
 * An expired file, in words: with datasets made from it before, those are
 * still there to use (the offer may have been answered with one of them).
 */
export function biUploadExpiredText(view: BiUploadView): string {
  return view.duplicates.length
    ? '这次没有重新导入，之前导入的数据可以继续使用。'
    : '这次上传已过期，没有导入。需要时请重新上传。';
}

export function biUploadFailureText(failure: BiUploadFailure | null): string {
  return failure ? kFailures[failure] : '这个文件没有导入成功，请重新上传。';
}

export function biSheetPhase(upload: BiSheetUploadView): BiSheetPhase {
  switch (upload.status) {
    case BiUploadStatus.NeedsConfirmation:
      return upload.schema
        ? { kind: 'confirm' }
        : { kind: 'working', label: '正在读取列…' };
    case BiUploadStatus.Confirmed:
      return { kind: 'working', label: '正在保存数据…' };
    case BiUploadStatus.Materialized:
      return { kind: 'working', label: '正在挑选值得看的图表…' };
    case BiUploadStatus.Ready:
      return { kind: 'proposals', draftIds: upload.proposalDraftIds };
    case BiUploadStatus.Failed:
      return { kind: 'failed', message: biUploadFailureText(upload.failure) };
    case BiUploadStatus.Deleted:
      return { kind: 'deleted' };
    default:
      return { kind: 'working', label: '正在读取列…' };
  }
}

/**
 * Whether its uploader may delete it now: not while the file is read or a
 * sheet is being saved or charted.
 */
export function isBiUploadDeletable(view: BiUploadView): boolean {
  if (
    view.status === BiUploadStatus.Uploaded ||
    view.status === BiUploadStatus.Deleted
  )
    return false;
  return !view.sheets.some(
    sheet =>
      sheet.upload?.status === BiUploadStatus.Uploaded ||
      sheet.upload?.status === BiUploadStatus.Confirmed ||
      sheet.upload?.status === BiUploadStatus.Materialized,
  );
}

/** What deleting it removes, for the confirmation. */
export function biUploadDeleteText(view: BiUploadView): string {
  return view.sheets.some(sheet => sheet.upload?.datasetId)
    ? '删除这次上传的记录。已经导入的数据不会删除，可以在数据库里单独删除。'
    : '删除后这次上传不会再出现，需要时可以重新上传。';
}

/** The system is still working on it: the card keeps asking. */
export function isBiUploadWorking(view: BiUploadView): boolean {
  if (view.status === BiUploadStatus.Uploaded) return true;
  if (view.status !== BiUploadStatus.Split) return false;
  return view.sheets.some(
    sheet => sheet.upload && biSheetPhase(sheet.upload).kind === 'working',
  );
}
